/**
 * Forecast V3 API Layer
 * TypeScript wrappers around existing forecast API utilities
 */

// @ts-ignore - JavaScript module without types
import { api } from '../../../utils/api';
// @ts-ignore - JavaScript module without types
import API_ENDPOINTS from '../../../config/apiConfig';
import type { BudgetExportFormat, ExportFormatSlots, ExportFormatTarget } from '../utils/exportFormats';

// Import existing API utilities
// Note: getForecastPnLDataV2 replaced with direct call to FORECAST_V3_PNL endpoint (faster)
// @ts-ignore - JavaScript module without types
import {
  createFinancialForecast,
  createBulkFinancialForecasts,
  clearForecastMonth,
} from '../../../utils/forecastApi';
// @ts-ignore - JavaScript module without types
import {
  getForecastAccounts,
  upsertForecastAccount,
} from '../../../utils/forecastAccountsApi';

import type {
  ForecastAccount,
  DriverType,
  AccountType,
  MonthlyExpandedValues,
  ZeroBaseDriverLayer,
  DriverRateInfo,
  DriverInputOverrideRow,
  DriverInputOverrideItem,
  LaborPositionConfig,
  AiBuildPreviewResult,
  AiBuildProgress,
  AiBuildRoomStatSeed,
  AiRoomsForecastPreviewResult,
} from '../types';

// ============ API Response Types ============

export interface ForecastPnLRecord {
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  accountGroup: string;
  department: string;
  departmentId?: number;
  monthNumber: number;
  year: number;
  budgetAmount: number;
  actualAmount: number;
  calculatedForecastAmount: number;
  savedForecastAmount: number | null;
  driverType: string | null;
  driverInput: number | null;
  roomsAvailable: number;
  roomsOccupied: number;
  occupancyRate: number;
  adr: number;
  revpar: number;
  hasDailyValues?: boolean;
}

export interface ForecastAccountRecord {
  id: number;
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  forecastDriver: string;
  inputMode: 'monthly' | 'daily';
  isDeleted: boolean;
}

// ============ Request Types ============

export interface FetchForecastParams {
  orgId: number;
  locationId: number;
  year: number;
  includeBlankDepartment?: boolean; // Default: false - filter out records with null/empty department
  metadataOnly?: boolean; // Version workspace gets amounts and room stats from V3 Calculate
}

export interface UpdateDriverParams {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  driverType: DriverType;
  driverInput: number | null;
  selectedDepartments?: string[];       // For "% of Selected Revenue" driver
  sourceChartOfAccountIds?: number[];   // For "% of Account" driver
}

export interface SaveForecastValueParams {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
  forecastAmount: number;
  driverType?: DriverType;
  driverInput?: number | null;
}

// ============ API Functions ============

/**
 * Fetch lightweight P&L data for a location/year
 * Uses the optimized V3 P&L endpoint (no forecast calculations - much faster)
 * Forecast calculations are done separately via calculateForecast()
 */
export async function fetchForecastPnLData(params: FetchForecastParams): Promise<ForecastPnLRecord[]> {
  // Use the lightweight V3 P&L endpoint instead of the slow V2 endpoint
  const response = await api.get(`${API_ENDPOINTS.FORECAST_V3_PNL}`, { params });
  const records = response.data as ForecastPnLRecord[];

  // Filter out records with blank/null department unless explicitly included
  if (!params.includeBlankDepartment) {
    return records.filter(record =>
      record.department && record.department.trim() !== ''
    );
  }

  return records;
}

/**
 * Fetch driver configurations for all accounts
 */
export async function fetchForecastAccounts(params: { orgId: number; locationId: number }): Promise<ForecastAccountRecord[]> {
  // The underlying API accepts chartOfAccountId as optional
  const data = await getForecastAccounts({ ...params, chartOfAccountId: undefined });
  return data as ForecastAccountRecord[];
}

/**
 * Update driver type and input for an account
 */
export async function updateAccountDriver(params: UpdateDriverParams): Promise<void> {
  await upsertForecastAccount({
    orgId: params.orgId,
    locationId: params.locationId,
    chartOfAccountId: params.chartOfAccountId,
    forecastDriver: params.driverType,
    inputMode: 'monthly',
  });
}

/**
 * Save a forecast value for a specific account/month
 */
export async function saveForecastValue(params: SaveForecastValueParams): Promise<void> {
  await createFinancialForecast({
    orgId: params.orgId,
    locationId: params.locationId,
    chartOfAccountId: params.chartOfAccountId,
    year: params.year,
    month: params.month,
    forecastAmount: params.forecastAmount,
    driverType: params.driverType || 'Static Entry',
    driverInput: params.driverInput || 0,
  });
}

/**
 * Save multiple forecast values in bulk
 */
export async function saveForecastValuesBulk(entries: SaveForecastValueParams[]): Promise<void> {
  const formattedEntries = entries.map(entry => ({
    orgId: entry.orgId,
    locationId: entry.locationId,
    chartOfAccountId: entry.chartOfAccountId,
    year: entry.year,
    month: entry.month,
    forecastAmount: entry.forecastAmount,
    driverType: entry.driverType || 'Static Entry',
    driverInput: entry.driverInput || 0,
  }));

  await createBulkFinancialForecasts(formattedEntries);
}

/**
 * Clear all forecast data for a specific account/month
 */
export async function clearMonthForecast(params: {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
}): Promise<void> {
  await clearForecastMonth(params);
}

// ============ Version API Types ============

export interface ForecastVersion {
  budgetReferenceYear?: number | null;
  id: string;
  orgId: number;
  locationId: number;
  name: string;
  description?: string;
  status: 'draft' | 'locked' | 'published';
  year: number;                              // The year this version covers
  versionType?: 'forecast' | 'budget';       // Type of version
  parentVersionId?: string;
  publishedAt?: string;
  publishNote?: string;
  lockedAt?: string;
  lockReason?: string;
  isDefault?: boolean;
  lockedMonths?: number[];                   // Array of locked months (1-12)
  roomStatSource?: 'pms' | 'budget' | 'actuals';  // Source for room statistics
  actualRollInEnabled?: boolean;             // Workspace toggle: Actuals + Forecast vs Forecast only
  createdBy: string;
  createdByName?: string;
  createdAt: string;
  updatedAt: string;
  // Deprecated - kept for backward compatibility
  baselineYear?: number;
}

export interface CreateVersionParams {
  orgId: number;
  locationId: number;
  name: string;
  description?: string;
  year: number;
  versionType?: 'forecast' | 'budget';
  parentVersionId?: string;
}

export interface LockMonthParams {
  month: number;                   // 1-12
  year?: number;                   // Defaults to version's year
  lockReason?: string;
}

export interface UnlockMonthParams {
  month: number;                   // 1-12
  year?: number;                   // Defaults to version's year
}

export interface GetLockedSnapshotParams {
  versionId: number;
  year: number;
  month: number;
}

export interface LockedAccountSnapshot {
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  department: string;
  calculatedAmount: number;  // Includes adjustments (baked in at lock time)
  baseAmount?: number;       // Before adjustments
  driverType: string;
  driverInput: number | null;
  hasOverride: boolean;
  overrideAmount: number | null;
  hasAdjustments?: boolean;
  adjustmentsTotal?: number;
  adjustments?: AppliedAdjustment[];  // Snapshot of adjustments applied at lock time
}

export interface LockedMonthSnapshot {
  id: string;
  versionId: number;
  orgId: number;
  locationId: number;
  year: number;
  month: number;
  monthKey: string;
  accountCount: number;
  accounts: LockedAccountSnapshot[];
  roomStats?: {
    availableRooms: number;
    occupiedRooms: number;
    occupancyRate: number;
  };
  lockedAt: string;
  lockedBy: string;
  lockReason: string | null;
}

export interface GetLockedSnapshotResponse {
  locked: boolean;
  snapshot: LockedMonthSnapshot | null;
  warning?: string;
}

export interface UpdateVersionParams {
  name?: string;
  description?: string;
  year?: number; // Changing the fiscal year re-keys every year-scoped row (overrides, daily values, locks, adjustments)
}

export interface LockVersionParams {
  year?: number;
  month?: number;
  lockReason?: string;
}

// ============ Version API Functions ============

/**
 * Fetch all forecast versions for a location
 */
export async function fetchVersions(params: {
  orgId: number;
  locationId: number;
}): Promise<ForecastVersion[]> {
  const response = await api.get(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?orgId=${params.orgId}&locationId=${params.locationId}`
  );
  return response.data;
}

/**
 * Fetch all forecast versions for a location and year
 * Returns monthly forecast versions for the specified year
 */
export async function fetchVersionsByYear(params: {
  orgId: number;
  locationId: number;
  year: number;
  versionType?: 'forecast' | 'budget';
}): Promise<ForecastVersion[]> {
  const queryParams = new URLSearchParams({
    orgId: params.orgId.toString(),
    locationId: params.locationId.toString(),
    year: params.year.toString(),
  });
  if (params.versionType) {
    queryParams.append('versionType', params.versionType);
  }
  const response = await api.get(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?${queryParams.toString()}`
  );
  return response.data;
}

/**
 * Lock a specific month within a version
 * The month becomes read-only and serves as baseline for trending
 * Creates a snapshot of all calculated values in Cosmos DB
 */
export async function lockMonth(versionId: string, params: LockMonthParams): Promise<{
  lockedMonths: number[];
  snapshotId: string;
  accountsLocked: number;
  message: string;
}> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=lockMonth&id=${versionId}`,
    params
  );
  return response.data;
}

/**
 * Unlock a specific month within a version
 * Returns the month to editable (draft) state
 * Deletes the snapshot from Cosmos DB
 */
export async function unlockMonth(versionId: string, params: UnlockMonthParams): Promise<{
  lockedMonths: number[];
  snapshotDeleted: boolean;
  message: string;
}> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=unlockMonth&id=${versionId}`,
    params
  );
  return response.data;
}

/**
 * Get locked month snapshot data from Cosmos DB
 * Returns the frozen account values if month is locked, or null if not locked
 */
export async function getLockedSnapshot(params: GetLockedSnapshotParams): Promise<GetLockedSnapshotResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getLockedSnapshot`,
    params
  );
  return response.data;
}

/**
 * Create a new draft version
 */
export async function createVersion(params: CreateVersionParams): Promise<ForecastVersion> {
  // Version creation participates in agent workflows and must use the same
  // Fastify route as workflow execution. The legacy Functions route can leave
  // the modal awaiting a response indefinitely in local/dev environments.
  const response = await api.post(API_ENDPOINTS.FORECAST_V3_VERSIONS_AI, params, { timeout: 180000 });
  return response.data;
}

/**
 * Update version metadata
 */
export async function updateVersion(id: string, params: UpdateVersionParams): Promise<ForecastVersion> {
  const response = await api.put(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?id=${id}`, params);
  return response.data;
}

/**
 * Delete a draft version (soft delete)
 */
export async function deleteVersion(id: string): Promise<void> {
  await api.delete(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?id=${id}`);
}

/**
 * Duplicate an existing version
 */
export async function duplicateVersion(id: string, name: string): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=duplicate&id=${id}`,
    { name }
  );
  return response.data;
}

/**
 * Publish a version (makes it the active forecast)
 */
export async function publishVersion(id: string, publishNote?: string): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=publish&id=${id}`,
    { publishNote }
  );
  return response.data;
}

/**
 * Lock a version (prevents further edits)
 */
export async function lockVersion(id: string, params?: LockVersionParams): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=lock&id=${id}`,
    params || {}
  );
  return response.data;
}

/**
 * Unlock a locked version (returns to draft status)
 */
export async function unlockVersion(id: string): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=unlock&id=${id}`,
    {}
  );
  return response.data;
}

/**
 * Set a version as the default for the location
 * This makes it the default view for all users accessing this location's forecast
 */
export async function setDefaultVersion(id: string): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=setDefault&id=${id}`,
    {}
  );
  return response.data;
}

// ============ V3 Override API ============

export interface SaveOverrideParams {
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
  overrideAmount: number | null;
  inputCurrency?: string | null;
  notes?: string;
}

export interface ForecastOverride {
  id: number;
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
  overrideAmount: number;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Save or update an override for a specific account/month in a version
 * Pass null for overrideAmount to remove the override
 */
export async function saveOverrideV3(params: SaveOverrideParams): Promise<ForecastOverride | { success: boolean; message: string }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=override`,
    params
  );
  return response.data;
}

/**
 * Bulk save/update/delete overrides in a single request.
 * Much faster than N individual saveOverrideV3 calls for department-level operations.
 */
export async function saveBulkOverridesV3(
  versionId: number,
  overrides: Omit<SaveOverrideParams, 'versionId'>[]
): Promise<{ success: boolean; created: number; updated: number; deleted: number; failed: number; total: number }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=bulkOverride`,
    { versionId, overrides }
  );
  return response.data;
}

// ============ V3 Driver-level monthly override API ============

export interface SaveDriverInputOverridesResponse {
  success: boolean;
  batchId: string;
  applied: number;
  created: number;
  updated: number;
  deleted: number;
  skippedLocked: { chartOfAccountId: number; year: number; month: number }[];
}

/**
 * Upsert / clear driver-level monthly overrides (rate, layer value, position
 * wage) — the driver stays live. value: null clears one override.
 */
export async function saveDriverInputOverridesV3(
  versionId: number,
  items: DriverInputOverrideItem[],
  batchId?: string
): Promise<SaveDriverInputOverridesResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=saveDriverInputOverrides`,
    { versionId, items, batchId }
  );
  return response.data;
}

/** Clear every driver-level monthly override on a version (or one account) */
export async function clearDriverInputOverridesV3(
  versionId: number,
  chartOfAccountId?: number
): Promise<{ success: boolean; batchId: string; cleared: number }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=clearDriverInputOverrides`,
    { versionId, chartOfAccountId }
  );
  return response.data;
}

// ============ V3 Adjustment API ============

export type AdjustmentType = 'FLAT' | 'PERCENT';

export interface ForecastAdjustment {
  id: number;
  versionId: number;
  chartOfAccountId: number;
  locationId: number;
  adjustmentType: AdjustmentType;
  value: number;  // e.g., 100000 for flat, 5 for 5%
  description: string | null;
  startMonth: string;  // ISO date string
  endMonth: string;    // ISO date string
  expiresAt: string | null;  // ISO date string
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAdjustmentParams {
  versionId: number;
  chartOfAccountId: number;
  locationId: number;
  adjustmentType: AdjustmentType;
  value: number;
  description?: string;
  startMonth: string;  // ISO date (first of month, e.g., '2025-01-01')
  endMonth: string;    // ISO date (first of month)
  expiresAt?: string;  // ISO date (optional)
}

export interface UpdateAdjustmentParams extends CreateAdjustmentParams {
  id: number;
}

export interface GetAdjustmentsParams {
  versionId: number;
  chartOfAccountId: number;
  locationId?: number;
  includeExpired?: boolean;
}

export interface GetAdjustmentsResponse {
  adjustments: ForecastAdjustment[];
  activeCount: number;
}

export interface AppliedAdjustment {
  id: number;
  type: AdjustmentType;
  description: string | null;
  inputValue: number;      // What user entered (100000 or 5)
  calculatedAmount: number; // Actual dollar impact
}

/**
 * Create a new adjustment for an account
 */
export async function createAdjustment(params: CreateAdjustmentParams): Promise<ForecastAdjustment> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=adjustment`,
    params
  );
  return response.data;
}

/**
 * Update an existing adjustment
 */
export async function updateAdjustment(params: UpdateAdjustmentParams): Promise<ForecastAdjustment> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=adjustment`,
    params
  );
  return response.data;
}

/**
 * Delete an adjustment (soft delete)
 */
export async function deleteAdjustment(id: number): Promise<{ success: boolean; message: string }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=deleteAdjustment`,
    { id }
  );
  return response.data;
}

/**
 * Get adjustments for an account
 */
export async function getAdjustments(params: GetAdjustmentsParams): Promise<GetAdjustmentsResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getAdjustments`,
    params
  );
  return response.data;
}

// ============ Compare API Types ============

export interface CompareVersionsParams {
  orgId: number;
  locationId: number;
  baseVersionId: number;
  compareVersionId: number;
  chartOfAccountIds?: number[];
  months?: string[]; // ['2025-01', '2025-02', ...]
}

export interface ComparisonItem {
  chartOfAccountId: number;
  month: string;
  baseAmount: number;
  compareAmount: number;
  variance: number;
  variancePercent: number;
  reasonCodes: string[];
  accountName?: string;
  accountNumber?: string;
  department?: string;
}

export interface AccountComparison {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  department: string;
  months: ComparisonItem[];
  totalBaseAmount: number;
  totalCompareAmount: number;
  totalVariance: number;
}

export interface CompareVersionsResponse {
  baseVersion: Pick<ForecastVersion, 'id' | 'name' | 'status'>;
  compareVersion: Pick<ForecastVersion, 'id' | 'name' | 'status'>;
  comparison: ComparisonItem[];
  byAccount: AccountComparison[];
  summary: {
    totalBaseAmount: number;
    totalCompareAmount: number;
    totalVariance: number;
    accountsChanged: number;
  };
}

// ============ Compare API Functions ============

/**
 * Compare two forecast versions
 */
export async function compareVersions(params: CompareVersionsParams): Promise<CompareVersionsResponse> {
  const response = await api.post(API_ENDPOINTS.FORECAST_V3_COMPARE, params);
  return response.data;
}

// ============ Calculate API Types ============

export interface DriverMetadata {
  type: string;
  category: string;
  description: string;
  requiresInput: boolean;
  inputType?: 'currency' | 'percentage' | 'number';
  inputLabel?: string;
}

export interface DriversByCategory {
  [category: string]: DriverMetadata[];
}

export interface CalculateForecastParams {
  orgId: number;
  locationId: number;
  versionId: number;
  chartOfAccountIds: number[];
  months: string[]; // ['2025-01', '2025-02', ...]
  includeExplanation?: boolean;
  roomStatSource?: 'pms' | 'budget' | 'actuals'; // Source for room stats: 'pms' (default), 'actuals', or 'budget'
}

export interface CalculationResult {
  chartOfAccountId: number;
  month: string;
  year: number;
  monthNumber: number;
  calculatedAmount: number;
  baseAmount?: number;  // Before adjustments
  // 'blended' = pinned days + live driver days; 'daily' = legacy full-month detach
  source: 'calculated' | 'override' | 'manual' | 'daily' | 'blended';
  driverType: string;
  driverInput: number | null;
  hasOverride: boolean;
  overrideAmount: number | null;
  hasDailyValues?: boolean;
  dailyValuesTotal?: number | null;
  dailyValuesCount?: number;
  dailyValuesMode?: 'blended' | 'legacy' | null;
  pinnedDaysCount?: number;
  liveDaysCount?: number;
  adjustments?: AppliedAdjustment[];  // Array of applied adjustments
  adjustmentsTotal?: number;  // Total adjustment amount
  hasAdjustments?: boolean;
  budget: number;
  actualAmount: number;
  lyBudget?: number;
  lyBudgetOccupiedRooms?: number;
  lyActual?: number;  // Last year same month actual
  // Department driver inheritance
  inheritsFromDepartment?: boolean;
  departmentDriverType?: string;
  driverSource?: 'account' | 'department' | 'none';
  // Fallback driver info (when department driver has OTB/RMS with fallback)
  usingFallbackDriver?: boolean;
  originalDepartmentDriverType?: string;
  // Selected departments for % of Selected Revenue driver
  selectedDepartments?: string[];
  // Source account IDs for % of Account driver
  sourceChartOfAccountIds?: number[];
  // Zero Base composable driver layers
  zeroBaseDriverLayers?: ZeroBaseDriverLayer[] | null;
  // Labor Config composable per-position driver configs
  laborPositionConfigs?: LaborPositionConfig[] | null;
  // Fallback driver type for REVPAR Projection
  fallbackDriverType?: string | null;
  // ---- Driver-level monthly overrides ----
  effectiveZeroBaseDriverLayers?: ZeroBaseDriverLayer[] | null;
  effectiveLaborPositionConfigs?: LaborPositionConfig[] | null;
  driverRate?: DriverRateInfo | null;
  baseDriverInput?: number | null;
  driverInputOverrides?: DriverInputOverrideRow[];
  hasDriverInputOverride?: boolean;
}

export interface CalculationExplanationResult {
  chartOfAccountId: number;
  month: string;
  formula: string;
  steps: string[];
  factors: Record<string, unknown>;
}

// Room stats values for a single metric (available, occupied, occupancy)
export interface RoomStatValues {
  availableRooms: number;
  occupiedRooms: number;
  occupancyRate: number;
}

// Room stats for a month with expanded view data (actual, forecast, budget)
export interface MonthlyRoomStats extends RoomStatValues {
  forecast: RoomStatValues;
  // Actuals carry recorded ADR/RevPAR (actual rooms revenue ÷ actual room
  // nights) so closed months can display them instead of the forecast pair
  actual: RoomStatValues & { roomsRevenue?: number; adr?: number | null; revpar?: number | null };
  budget: RoomStatValues;
  lyBudget?: RoomStatValues;
  // Projected values (used by drivers - may include overrides)
  projected?: {
    availableRooms: number;
    occupiedRooms: number;
    occupiedRoomsSource?: string;
    // Derived from the rooms revenue FORECAST (driver + pins + overrides) —
    // the occupancy section folds in from whatever rooms revenue forecasts
    roomsRevenue?: number;
    adr?: number | null;
    revpar?: number | null;
  };
  // Override info for UI display
  overrides?: {
    availableRooms: number | null;
    occupiedRooms: number | null;
    hasAvailableOverride: boolean;
    hasOccupiedOverride: boolean;
  };
}

export interface CalculateForecastResponse {
  version: {
    id: number;
    name: string;
    status: string;
    financialCloseDay?: number;
    actualRollInEnabled?: boolean;
  };
  results: CalculationResult[];
  roomStats?: Record<string, MonthlyRoomStats>;
  roomStatSource?: 'pms' | 'budget' | 'actuals';
  financialCloseDay?: number;
  explanations?: CalculationExplanationResult[];
  performance: {
    accountsCalculated: number;
    monthsCalculated: number;
    totalCalculations: number;
    durationMs: number;
    cacheHit: boolean;
  };
}

// ============ Calculate API Functions ============

/**
 * Calculate forecasts for specified accounts and months (on-demand)
 */
export async function calculateForecast(params: CalculateForecastParams): Promise<CalculateForecastResponse> {
  const response = await api.post(API_ENDPOINTS.FORECAST_V3_CALCULATE, params);
  return response.data;
}

/**
 * Fetch all available driver types
 */
export async function fetchDriverTypes(): Promise<DriverMetadata[]> {
  const response = await api.get(`${API_ENDPOINTS.FORECAST_V3_CALCULATE}?action=drivers`);
  return response.data.drivers;
}

/**
 * Fetch driver types grouped by category
 */
export async function fetchDriversByCategory(): Promise<DriversByCategory> {
  const response = await api.get(`${API_ENDPOINTS.FORECAST_V3_CALCULATE}?action=drivers-by-category`);
  return response.data;
}

// ============ V3 Driver Assignment API ============

export interface UpdateDriverAssignmentParams {
  versionId: number;
  chartOfAccountId: number;
  driverType: string;
  driverInput: number | null;
  effectiveStartMonth?: number;
  effectiveEndMonth?: number;
  selectedDepartments?: string[];       // For "% of Selected Revenue" driver
  sourceChartOfAccountIds?: number[];   // For "% of Account" driver
  clearOverrides?: boolean;  // If true, clears all overrides for this account (used when switching from Manual driver)
  fallbackDriverType?: 'Budget' | 'LY Same Month' | null;  // Fallback for REVPAR Projection future months
  zeroBaseDriverLayers?: ZeroBaseDriverLayer[];  // For "Zero Base" composable driver
  laborPositionConfigs?: LaborPositionConfig[];  // For "Labor Config" per-position composable driver
}

export interface DriverAssignment {
  id: number;
  versionId: number;
  chartOfAccountId: number;
  driverType: string;
  driverInput: number | null;
  effectiveStartMonth: number;
  effectiveEndMonth: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Update driver assignment for an account in a version (V3)
 */
export async function updateDriverAssignmentV3(params: UpdateDriverAssignmentParams): Promise<DriverAssignment> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=driver`, params);
  return response.data;
}

// ============ Bulk Driver Assignment API ============

export interface BulkDriverAssignment {
  chartOfAccountId: number;
  driverType: string;
  driverInput: number | null;
  inputMode?: 'monthly' | 'daily';
  effectiveStartMonth?: number;
  effectiveEndMonth?: number;
  selectedDepartments?: string[];       // For "% of Selected Revenue" driver
  sourceChartOfAccountIds?: number[];   // For "% of Account" driver
  fallbackDriverType?: 'Budget' | 'LY Same Month' | null;  // Fallback for REVPAR Projection future months
  laborPositionConfigs?: string | null;  // JSON payload for the "Labor Config" driver
}

export interface BulkDriverRequest {
  versionId: number;
  assignments: BulkDriverAssignment[];
}

export interface BulkDriverResult {
  chartOfAccountId: number;
  status: 'success' | 'error';
  error?: string;
}

export interface BulkDriverResponse {
  success: boolean;
  updated: number;
  failed: number;
  results: BulkDriverResult[];
}

/**
 * Update driver assignments for multiple accounts in bulk (V3)
 * All updates are applied in a single transaction
 */
export async function updateDriversBulk(params: BulkDriverRequest): Promise<BulkDriverResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=bulkDriver`, params);
  return response.data;
}

/**
 * Commit an AI driver build (V3). Same transactional writer as bulkDriver,
 * plus: 'OTB Forecast' picks persist as DEPARTMENT drivers, and a budget
 * build's rooms baseline (roomStatSeed) lands as forecastRoomStatOverride
 * rows in the same revertable batch — without it every $/ORN driver on a
 * future budget year computes $0.
 */
export async function commitAiDriverBuild(params: BulkDriverRequest & {
  roomStatSeed?: { seed: Record<string, { occupiedRooms?: number; availableRooms?: number }>; sources?: Record<string, string> } | null;
}): Promise<BulkDriverResponse & { roomStatsSeeded?: number; departmentDriversWritten?: number }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=aiBuildCommit`, params);
  return response.data;
}

// ============ Data Transformation Helpers ============

/**
 * Transform raw API data into grouped grid data structure
 */
export function transformPnLDataToGrid(records: ForecastPnLRecord[]): {
  accounts: Map<number, TransformedAccount>;
  totals: {
    revenue: Record<string, number>;
    expense: Record<string, number>;
    netIncome: Record<string, number>;
  };
} {
  const accounts = new Map<number, TransformedAccount>();
  const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

  // Initialize totals
  const totals = {
    revenue: {} as Record<string, number>,
    expense: {} as Record<string, number>,
    netIncome: {} as Record<string, number>,
  };

  monthKeys.forEach(key => {
    totals.revenue[key] = 0;
    totals.expense[key] = 0;
    totals.netIncome[key] = 0;
  });

  // Group records by account
  records.forEach(record => {
    const monthKey = monthKeys[record.monthNumber - 1];
    if (!monthKey) return;

    // Only include Revenue and Expense accounts in P&L view (like budgeting module)
    const rawAccountType = (record.accountType || '').toLowerCase();
    const isRevenue = rawAccountType === 'revenue';
    const isExpense = rawAccountType === 'expense';
    if (!isRevenue && !isExpense) {
      return; // Skip non-P&L accounts (Asset, Liability, Equity, etc.)
    }

    // Filter out accounts with null department (like budgeting module)
    if (!record.department && !record.accountGroup) {
      return;
    }

    // Get or create account entry
    if (!accounts.has(record.chartOfAccountId)) {
      accounts.set(record.chartOfAccountId, {
        id: record.chartOfAccountId,
        chartOfAccountId: record.chartOfAccountId,
        accountNumber: record.accountNumber,
        accountName: record.accountName,
        accountType: isRevenue ? 'Revenue' : 'Expense',
        department: record.department || record.accountGroup || 'Other',
        driverType: (record.driverType || 'Manual') as DriverType,
        driverInput: record.driverInput,
        monthlyValues: {},
        total: 0,
      });
    }

    const account = accounts.get(record.chartOfAccountId)!;

    // Use saved amount if available, otherwise calculated amount
    const forecastValue = record.savedForecastAmount ?? record.calculatedForecastAmount ?? 0;
    account.monthlyValues[monthKey] = forecastValue;

    // Track which months have daily values
    if (record.hasDailyValues) {
      if (!account.monthlyDailyValues) account.monthlyDailyValues = {};
      account.monthlyDailyValues[monthKey] = true;
    }

    // Update totals
    const accountType = account.accountType;
    if (accountType === 'Revenue') {
      totals.revenue[monthKey] = (totals.revenue[monthKey] || 0) + forecastValue;
    } else {
      totals.expense[monthKey] = (totals.expense[monthKey] || 0) + forecastValue;
    }
  });

  // Calculate account totals and net income
  accounts.forEach(account => {
    account.total = Object.values(account.monthlyValues).reduce((sum, val) => sum + (val || 0), 0);
  });

  monthKeys.forEach(key => {
    totals.netIncome[key] = totals.revenue[key] - totals.expense[key];
  });

  return { accounts, totals };
}

export interface TransformedAccount {
  id: number;
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  department: string;
  departmentId?: number;  // FK to chartOfAccountDepartments
  driverType: DriverType;
  driverInput: number | null;
  selectedDepartments?: string[];       // For % of Selected Revenue driver
  sourceChartOfAccountIds?: number[];   // For % of Account driver
  zeroBaseDriverLayers?: ZeroBaseDriverLayer[];  // For Zero Base composable driver
  laborPositionConfigs?: LaborPositionConfig[];  // For Labor Config composable driver
  monthlyValues: Record<string, number>;
  explanationFactors?: Record<string, Record<string, unknown>>; // { jan: {...}, feb: {...}, ... }
  monthlyOverrides?: Record<string, boolean>; // Track which months have overrides { jan: true, feb: false, ... }
  monthlyDailyValues?: Record<string, boolean>; // Track which months have daily values
  monthlyDailyMode?: Record<string, 'blended' | 'legacy'>; // blended = pins + live driver; legacy = static detach
  driverError?: string | null; // calculator: assigned driver not eligible for this account (amount 0)
  monthlyPinnedCounts?: Record<string, number>; // Pinned-day count per month
  // Driver-level monthly overrides
  monthlyDriverRate?: Record<string, DriverRateInfo | null>;
  monthlyZeroBaseLayers?: Record<string, ZeroBaseDriverLayer[]>;
  monthlyLaborConfigs?: Record<string, LaborPositionConfig[]>;
  monthlyDriverInputOverrides?: Record<string, DriverInputOverrideRow[]>;
  driverInputOverrideCount?: number;
  monthlyExpanded?: Record<string, MonthlyExpandedValues>; // Expanded values for Budget/Actual/Forecast columns
  total: number;
  // Adjustment tracking (aggregated from all months)
  hasAdjustments?: boolean;
  adjustmentsCount?: number;
  adjustmentsTotal?: number;
  // Department driver inheritance
  inheritsFromDepartment?: boolean;
  departmentDriverType?: string | null;  // 'OTB Forecast' | 'RMS Forecast'
  driverSource?: 'account' | 'department' | 'none';
  // Fallback driver info (when department driver has OTB/RMS with fallback)
  usingFallbackDriver?: boolean;
  originalDepartmentDriverType?: string | null;
}

/**
 * Group accounts by type (Revenue/Expense)
 */
function accountNumberSortValue(accountNumber?: string | null): number {
  const normalized = String(accountNumber || '').replace(/[^0-9.]/g, '');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
}

function compareAccountNumbers(a?: string | null, b?: string | null): number {
  const numericDiff = accountNumberSortValue(a) - accountNumberSortValue(b);
  if (numericDiff !== 0) return numericDiff;
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });
}

export function groupAccountsByType(accounts: Map<number, TransformedAccount>): {
  revenue: TransformedAccount[];
  expense: TransformedAccount[];
} {
  const revenue: TransformedAccount[] = [];
  const expense: TransformedAccount[] = [];

  accounts.forEach(account => {
    if (account.accountType === 'Revenue') {
      revenue.push(account);
    } else {
      expense.push(account);
    }
  });

  // Sort by account number
  revenue.sort((a, b) => compareAccountNumbers(a.accountNumber, b.accountNumber));
  expense.sort((a, b) => compareAccountNumbers(a.accountNumber, b.accountNumber));

  return { revenue, expense };
}

// ============ Daily Values API Types ============

export interface DailyValue {
  day: number;
  date: string;
  dayOfWeek: string;
  isWeekend: boolean;
  amount: number | null;
  adr: number | null;          // Daily ADR (stat account mode, entered by user)
  roomNights: number | null;   // Daily room nights (stat account mode, entered by user)
  notes: string | null;
  spreadPattern: string | null;
  hasValue: boolean;
  hasStatValues: boolean;      // true when adr + roomNights are set
  isPinned: boolean;           // pinned day on a live driver (blend mode)
}

export interface DailyValuesSummary {
  monthlyTotal: number;
  monthlyRoomNights: number;    // Sum of daily room nights (stat account mode)
  weightedADR: number;          // monthlyTotal / monthlyRoomNights
  daysWithValues: number;
  daysWithStatValues: number;   // Days with both adr and roomNights set
  daysRemaining: number;
  hasDailyValues: boolean;
  pinnedCount: number;          // Days saved as pins (isPinned=1)
  allPinned: boolean;           // Every saved day is a pin → month blends with live driver
}

export interface GetDailyValuesParams {
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
}

export interface GetDailyValuesResponse {
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
  daysInMonth: number;
  values: DailyValue[];
  summary: DailyValuesSummary;
}

export interface SaveDailyValuesParams {
  versionId: number;
  chartOfAccountId: number;
  locationId: number;
  year: number;
  month: number;
  values: Array<{
    day: number;
    amount?: number | null;      // Optional in stat mode; backend computes from adr * roomNights
    inputCurrency?: string | null;
    adr?: number | null;         // Stat account mode: daily ADR (entered)
    roomNights?: number | null;  // Stat account mode: daily room nights (entered)
    notes?: string;
  }>;
  inputCurrency?: string | null;
  spreadPattern?: string;
  /** Save posted days as PINS on a live driver (blend mode). Unposted days stay live. */
  pinMode?: boolean;
  /** Flip a month's existing rows to the posted mode instead of 409ing on a mixed month. */
  convert?: boolean;
}

export interface SaveDailyValuesResponse {
  success: boolean;
  savedCount: number;
  clearedCount: number;
  convertedCount?: number;
  skippedPastDays?: number;   // pins on already-actualized days are skipped
  monthlyTotal: number;
  monthlyRoomNights: number;
  weightedADR: number;
  batchId?: string | null;    // pin saves are revertable audit batches
}

/** 409 body when a save would mix pinned and legacy rows in one month */
export interface DailyValuesConflictResponse {
  error: string;
  code: 'LEGACY_DAILY_EXISTS' | 'PINNED_DAILY_EXISTS';
  legacyCount?: number;
  pinnedCount?: number;
}

export interface ClearDailyValuesParams {
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
}

export interface ClearDailyValuesResponse {
  success: boolean;
  clearedCount: number;
  previousTotal: number;
}

export type SpreadPattern = 'even' | 'weekend' | 'ly' | 'otb';

export interface SpreadMonthlyParams {
  versionId?: number;
  chartOfAccountId?: number;
  locationId?: number;
  year: number;
  month: number;
  totalAmount: number;
  pattern: SpreadPattern;
  save?: boolean;
}

export interface SpreadMonthlyResponse {
  year: number;
  month: number;
  pattern: string;
  totalAmount: number;
  daysInMonth: number;
  values: Array<{
    day: number;
    date: string;
    dayOfWeek: string;
    isWeekend: boolean;
    amount: number;
  }>;
  calculatedTotal: number;
}

export interface DailyReferenceOtb {
  day: number;
  date: Date;
  otbRevenue: number;
  otbRooms: number;
  projectedRevenue: number;
  projectedRooms: number;
}

export interface DailyReferenceRms {
  day: number;
  date: Date;
  revenue: number;
  roomsSold: number;
  occupancy: number;
  dayOfWeek: string;
}

export interface DailyReferenceLy {
  day: number;
  date: Date;
  amount: number;
}

export interface GetDailyReferenceParams {
  locationId: number;
  year: number;
  month: number;
  chartOfAccountId?: number;
}

export interface GetDailyReferenceResponse {
  year: number;
  month: number;
  daysInMonth: number;
  weekendDays: number[];
  otb: {
    data: DailyReferenceOtb[];
    total: number;
    hasData: boolean;
  };
  rms: {
    data: DailyReferenceRms[];
    total: number;
    hasData: boolean;
  };
  ly: {
    data: DailyReferenceLy[];
    total: number;
    hasData: boolean;
    year: number;
  };
}

// ============ Daily Values API Functions ============

/**
 * Get daily values for an account/month
 */
export async function getDailyValues(params: GetDailyValuesParams): Promise<GetDailyValuesResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDailyValues`,
    params
  );
  return response.data;
}

/**
 * Save daily values for an account/month
 */
export async function saveDailyValues(params: SaveDailyValuesParams): Promise<SaveDailyValuesResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=saveDailyValues`,
    params
  );
  return response.data;
}

/**
 * Clear all daily values for an account/month (reverts to driver calculation)
 */
export async function clearDailyValues(params: ClearDailyValuesParams): Promise<ClearDailyValuesResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=clearDailyValues`,
    params
  );
  return response.data;
}

// ============ Driver Daily Breakdown (pin-merge blending) ============

export interface DriverDailyBreakdownDay {
  day: number;
  date: string;              // 'YYYY-MM-DD'
  isActual: boolean;         // before hotel-local today (already-realized day)
  live: {
    amount: number;          // account-level (proportional share applied)
    rooms: number | null;    // hotel-level rooms for the day (OTB/actual/RMS)
    adr: number | null;
    source: 'actual' | 'otb' | 'rms' | 'pace' | 'fallback';
  };
  pin: {
    amount: number;
    roomNights: number | null;
    adr: number | null;
  } | null;
}

export interface DriverDailyBreakdownResponse {
  dailyCapable: boolean;     // false → fall back to the classic daily-entry UX
  driverType: string;
  driverSource: 'account' | 'department' | 'none';
  usingFallbackDriver: boolean;
  originalDepartmentDriverType: string | null;
  proportionalShare: number | null;
  daysInMonth: number;
  localToday: string | null; // hotel-local 'YYYY-MM-DD'
  monthMode: 'live' | 'blended' | 'legacy';
  days: DriverDailyBreakdownDay[];
  totals: {
    live: number;
    pinned: number;
    blended: number;
    driverMonthly: number;
  } | null;
}

export interface GetDriverDailyBreakdownParams {
  versionId: number;
  chartOfAccountId: number;
  year: number;
  month: number;
}

/**
 * Day-level live driver series + pin status for one account/month.
 * Powers the Daily Entry live/pin view for OTB/RMS/REVPAR-driven accounts.
 */
export async function getDriverDailyBreakdown(params: GetDriverDailyBreakdownParams): Promise<DriverDailyBreakdownResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDriverDailyBreakdown`,
    params
  );
  return response.data;
}

/**
 * Spread a monthly total across days using a pattern
 * Set save=true to persist to database
 */
export async function spreadMonthly(params: SpreadMonthlyParams): Promise<SpreadMonthlyResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=spreadMonthly`,
    params
  );
  return response.data;
}

/**
 * Get reference data (OTB, RMS, LY) for daily entry
 */
export async function getDailyReference(params: GetDailyReferenceParams): Promise<GetDailyReferenceResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDailyReference`,
    params
  );
  return response.data;
}

// ============ Linked Stat Account API Types ============

export interface GetLinkedStatAccountParams {
  locationId: number;
  orgId: number;
  revenueChartOfAccountId: number;
  versionId?: number;
  year?: number;
  month?: number;
}

export interface GetLinkedStatAccountResponse {
  statAccount: StatAccountMapping | null;
  monthlyRN: number | null;
  monthlyADR: number | null;
}

/**
 * Check if a revenue GL account has a linked stat account (Occupied Rooms mapping)
 */
export async function getLinkedStatAccount(params: GetLinkedStatAccountParams): Promise<GetLinkedStatAccountResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getLinkedStatAccount`,
    params
  );
  return response.data;
}

// ============ Department Driver API Types ============

export type DepartmentDriverType = 'Room Forecast' | 'OTB Forecast' | 'RMS Forecast' | '% of Revenue' | '% of Selected Revenue';

// Fallback drivers for future months when OTB/RMS data isn't available
export type FallbackDriverType = 'LY Same Month' | 'Budget' | null;

export interface DepartmentDriver {
  id: number;
  versionId: number;
  locationId: number;
  departmentName: string;
  accountType: AccountType;  // Scopes driver to Revenue or Expense accounts
  driverType: DepartmentDriverType;
  driverInput: number | null;
  inputMode: string;
  effectiveStartMonth: number;
  effectiveEndMonth: number;
  otbLeadDays: number;  // Days before month to start using OTB (default -5)
  fallbackDriverType: FallbackDriverType;  // What to use outside OTB window
  selectedDepartments?: string[];  // For "% of Selected Revenue" - which revenue departments to include
  forecastSource?: string | null;  // For 'Room Forecast': 'otb' | 'projected' | '<source>:<code>'
  createdAt?: string;
  updatedAt?: string;
}

export interface CascadeOptions {
  clearAccountDrivers: boolean;
  clearAccountOverrides: boolean;
  clearAccountAdjustments: boolean;
  clearAccountNotes: boolean;
}

export interface DepartmentAccountInfo {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  hasAccountDriver: boolean;
  accountDriverType: string | null;
  inheritsFromDepartment: boolean;
  hasOverrides: boolean;
  hasAdjustments: boolean;
  hasDailyValues: boolean;
}

export interface GetDepartmentDriverParams {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;  // FK-based lookup (preferred over departmentName)
  accountType: AccountType;
}

export interface SaveDepartmentDriverParams {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;  // FK-based lookup (preferred over departmentName)
  accountType: AccountType;
  driverType: DepartmentDriverType;
  driverInput?: number | null;
  effectiveStartMonth?: number;
  effectiveEndMonth?: number;
  otbLeadDays?: number;  // Days before month to start using OTB (default -5)
  fallbackDriverType?: FallbackDriverType;  // What to use outside OTB window
  selectedDepartments?: string[];  // For "% of Selected Revenue" - which revenue departments to include
  forecastSource?: string | null;  // For 'Room Forecast': 'otb' | 'projected' | '<source>:<code>'
  cascadeOptions?: CascadeOptions;
}

export interface SaveDepartmentDriverResponse {
  success: boolean;
  departmentDriver: DepartmentDriver;
  affectedAccounts?: number;
}

export interface ClearDepartmentDriverParams {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;  // FK-based lookup (preferred over departmentName)
  accountType: AccountType;
}

export interface GetDepartmentAccountsParams {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;  // FK-based lookup (preferred over departmentName)
  accountType: AccountType;
}

// ============ Department Driver API Functions ============

// A forecast source selectable by the 'Room Forecast' department driver
export interface RoomForecastSource {
  value: string;   // 'otb' | 'projected' | '<source>:<code>'
  label: string;
  description: string;
  source?: string;
  code?: string;
}

/**
 * List the forecast sources available to the 'Room Forecast' driver for a
 * location (the two system slots plus every normalized forecast type with
 * data for this location). versionId scopes the per-version AI rooms
 * forecast source to the version asking.
 */
export async function getRoomForecastSources(locationId: number, versionId?: number): Promise<RoomForecastSource[]> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getRoomForecastSources`,
    { locationId, versionId }
  );
  return response.data?.sources || [];
}

/**
 * Get department driver configuration
 */
export async function getDepartmentDriver(params: GetDepartmentDriverParams): Promise<DepartmentDriver | null> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDepartmentDriver`,
    params
  );
  return response.data;
}

/**
 * Save department driver with optional cascade options
 */
export async function saveDepartmentDriver(params: SaveDepartmentDriverParams): Promise<SaveDepartmentDriverResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=saveDepartmentDriver`,
    params
  );
  return response.data;
}

/**
 * Clear department driver
 */
export async function clearDepartmentDriver(params: ClearDepartmentDriverParams): Promise<{ success: boolean }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=clearDepartmentDriver`,
    params
  );
  return response.data;
}

/**
 * Get accounts in department with inheritance status
 */
export async function getDepartmentAccounts(params: GetDepartmentAccountsParams): Promise<DepartmentAccountInfo[]> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDepartmentAccounts`,
    params
  );
  return response.data;
}

// ============ Account Analysis API Types ============

export interface HistoricalMonth {
  month: string;          // "2025-02"
  displayMonth: string;   // "Feb"
  year: number;
  monthNum: number;
  value: number;
  isForecast: boolean;
}

export interface AnalysisStats {
  min: number;
  max: number;
  mean: number;
  stdDev: number;
  variance: number;     // Coefficient of variation as percentage
  range: number;
}

export interface AnalysisInsight {
  text: string;
  statusMessage: string;
  statusType: 'success' | 'warning' | 'danger';
  contextNote: string | null;
}

export type ForecastStatus = 'in_range' | 'high_warning' | 'low_warning' | 'high_danger' | 'low_danger';

export interface AccountAnalysis {
  chartOfAccountId: number;
  locationId: number;
  versionId?: number;
  accountName: string;
  accountNumber: string;
  historicalMonths: HistoricalMonth[];
  currentForecast: HistoricalMonth | null;
  stats: AnalysisStats | null;
  forecastStatus: ForecastStatus | null;
  forecastPosition: number;
  insight: AnalysisInsight;
  /** PMS occupied rooms per historical month — key is "YYYY-MM" */
  roomStatsPerMonth?: Record<string, number>;
  /** Actual revenue per department per historical month — key is "YYYY-MM", value is { deptName: amount, _total: total } */
  revenueByDepartmentPerMonth?: Record<string, Record<string, number>>;
  /** Forecasted revenue by department for the forecast month — key is deptName or "_total" */
  forecastRevenueByDepartment?: Record<string, number>;
  metadata: {
    calculatedAt: string;
    dataPointsUsed: number;
    hasMissingMonths: boolean;
  };
}

export interface GetAccountAnalysisParams {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  versionId?: number;
  forecastMonth: number;  // 1-12
  forecastYear: number;
}

/**
 * Get historical analysis data for an account
 */
export async function getAccountAnalysis(params: GetAccountAnalysisParams): Promise<AccountAnalysis> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getAccountAnalysis`,
    params
  );
  return response.data;
}

// ============ Revenue Departments ============

export interface GetRevenueDepartmentsParams {
  orgId: number;
  locationId: number;
}

export interface RevenueDepartment {
  name: string;
  accountCount: number;
}

export interface GetRevenueDepartmentsResponse {
  departments: RevenueDepartment[];
}

/**
 * Get distinct revenue departments for a location
 * Used for "% of Selected Revenue" driver department selection
 */
export async function getRevenueDepartments(params: GetRevenueDepartmentsParams): Promise<GetRevenueDepartmentsResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getRevenueDepartments`,
    params
  );
  return response.data;
}

// ============ GL Transactions API Types ============

export interface GLTransaction {
  id: number;
  chartOfAccountId: number;
  transactionDate: string;
  transactionType: string;
  transactionDesc: string | null;
  transactionAmount: number;
  currency?: string | null;
  vendor: string | null;
  /** Source-invoice PDF link (Plate IQ via NetSuite ETL) when the line came from a bill */
  sourceDocumentUrl?: string | null;
}

export interface GLTransactionsResponse {
  data: GLTransaction[];
  pagination: {
    page: number;
    limit: number;
    totalRecords: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface FetchGLTransactionsParams {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  year: number;
  month: number; // 1-12
}

// ============ GL Transactions API Functions ============

export async function fetchGLTransactions(params: FetchGLTransactionsParams): Promise<GLTransactionsResponse> {
  const { orgId, locationId, chartOfAccountId, year, month } = params;
  // Build date range for the month
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const response = await api.get(API_ENDPOINTS.TRANSACTIONS, {
    params: {
      orgId,
      locationId,
      chartOfAccountId,
      startDate,
      endDate,
      limit: 500,
    },
  });
  return response.data;
}

// ============ Department Analysis API Types ============

export interface DepartmentAnalysis {
  departmentName: string;
  locationId: number;
  versionId?: number;
  accountType: 'Revenue' | 'Expense';
  accountCount: number;
  historicalMonths: HistoricalMonth[];
  currentForecast: HistoricalMonth | null;
  stats: AnalysisStats | null;
  forecastStatus: ForecastStatus | null;
  forecastPosition: number;
  insight: AnalysisInsight;
  /** PMS occupied rooms per historical month — key is "YYYY-MM" */
  roomStatsPerMonth?: Record<string, number>;
  /** Actual revenue per department per historical month — key is "YYYY-MM", value is { deptName: amount, _total: total } */
  revenueByDepartmentPerMonth?: Record<string, Record<string, number>>;
  /** Forecasted revenue by department for the forecast month — key is deptName or "_total" */
  forecastRevenueByDepartment?: Record<string, number>;
  metadata: {
    calculatedAt: string;
    dataPointsUsed: number;
    hasMissingMonths: boolean;
  };
}

export interface GetDepartmentAnalysisParams {
  orgId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  accountType: 'Revenue' | 'Expense';
  versionId?: number;
  forecastMonth: number;  // 1-12
  forecastYear: number;
}

/**
 * Get historical analysis data for a department (aggregated across all accounts)
 */
export async function getDepartmentAnalysis(params: GetDepartmentAnalysisParams): Promise<DepartmentAnalysis> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getDepartmentAnalysis`,
    params
  );
  return response.data;
}

// ============ Room Stat Override API Types ============

export type RoomStatType = 'availableRooms' | 'occupiedRooms';

export interface SaveRoomStatOverrideParams {
  versionId: number;
  year: number;
  month: number;
  roomStatType: RoomStatType;
  overrideValue: number | null;
  notes?: string;
}

export interface RoomStatOverride {
  id: number;
  versionId: number;
  year: number;
  month: number;
  roomStatType: RoomStatType;
  overrideValue: number;
  notes: string | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

// ============ Room Stat Override API Functions ============

/**
 * Save or update a room stat override for a specific version/year/month
 * Pass null for overrideValue to remove the override
 */
export async function saveRoomStatOverride(params: SaveRoomStatOverrideParams): Promise<RoomStatOverride | { success: boolean; message: string }> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=roomStatOverride`,
    params
  );
  return response.data;
}

// ============ Room Stat Source API ============

export interface UpdateRoomStatSourceParams {
  versionId: number;
  roomStatSource: 'pms' | 'budget' | 'actuals';
}

/**
 * Update the room stat source for a version
 * This setting determines which data source is used for room statistics (ARN/ORN)
 */
export async function updateRoomStatSource(params: UpdateRoomStatSourceParams): Promise<any> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=updateRoomStatSource`,
    params
  );
  return response.data;
}

export interface UpdateActualRollInPreferenceParams {
  versionId: number;
  actualRollInEnabled: boolean;
}

/**
 * Persist the Forecast V3 Actuals + Forecast / Forecast only workspace preference.
 */
export async function updateActualRollInPreference(params: UpdateActualRollInPreferenceParams): Promise<ForecastVersion> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=updateActualRollInPreference`,
    params
  );
  return response.data;
}

// ============ Labor API Types ============

export interface LaborCoaMapping {
  id: number;
  orgId: number;
  locationId: number;
  departmentId: number;
  departmentName?: string;
  chartOfAccountId: number;
  accountNumber?: string;
  accountName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateLaborCoaMappingParams {
  orgId: number;
  locationId: number;
  departmentId: number;
  chartOfAccountId: number;
}

export interface LaborDetailEmployee {
  employeeId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  hireDate: string | null;
  status: string;
  actualHours: number;
  actualWages: number;
  daysWorked: number;
  avgHourlyRate: number;
}

export interface LaborDetailPosition {
  positionId: number;
  positionName: string;
  departmentId: number;
  departmentName: string;
  actualHours: number;
  actualWages: number;
  projectedHours: number;
  projectedWages: number;
  totalHours: number;
  totalWages: number;
  hpor: number;  // Hours Per Occupied Room
  dpor: number;  // Dollars Per Occupied Room
  employees: LaborDetailEmployee[];
}

export interface LaborDetailAccount {
  chartOfAccountId: number | null;
  accountNumber: string;
  accountName: string;
  actualHours: number;
  actualWages: number;
  projectedHours: number;
  projectedWages: number;
  totalHours: number;
  totalWages: number;
  hpor: number;  // Hours Per Occupied Room
  dpor: number;  // Dollars Per Occupied Room
  positions: LaborDetailPosition[];
}

export interface LaborDetailMeta {
  orgId: number;
  locationId: number;
  year: number;
  month: number;
  daysInMonth: number;
  daysElapsed: number;
  daysRemaining: number;
  isCurrentMonth: boolean;
  occupiedRooms: number;
}

export interface LaborDetailResponse {
  data: LaborDetailAccount[];
  meta: LaborDetailMeta;
}

export interface GetLaborDetailParams {
  orgId: number;
  locationId: number;
  year: number;
  month: number;
}

// ============ Labor API Functions ============

/**
 * Get labor-to-GL account mappings for a location
 */
export async function getLaborCoaMappings(orgId: number, locationId: number): Promise<LaborCoaMapping[]> {
  const response = await api.get(
    `${API_ENDPOINTS.LABOR_COA_MAPPINGS}?orgId=${orgId}&locationId=${locationId}`
  );
  return response.data;
}

/**
 * Create a new labor-to-GL account mapping
 */
export async function createLaborCoaMapping(params: CreateLaborCoaMappingParams): Promise<LaborCoaMapping> {
  const response = await api.post(API_ENDPOINTS.LABOR_COA_MAPPINGS, params);
  return response.data;
}

/**
 * Update a labor-to-GL account mapping
 */
export async function updateLaborCoaMapping(id: number, chartOfAccountId: number): Promise<LaborCoaMapping> {
  const response = await api.put(
    `${API_ENDPOINTS.LABOR_COA_MAPPINGS}?id=${id}`,
    { chartOfAccountId }
  );
  return response.data;
}

/**
 * Delete a labor-to-GL account mapping
 */
export async function deleteLaborCoaMapping(id: number): Promise<{ success: boolean; message: string }> {
  const response = await api.delete(`${API_ENDPOINTS.LABOR_COA_MAPPINGS}?id=${id}`);
  return response.data;
}

/**
 * Get detailed labor data by GL account, position, and employee
 */
export async function getLaborDetailData(params: GetLaborDetailParams): Promise<LaborDetailResponse> {
  const { orgId, locationId, year, month } = params;
  const response = await api.get(
    `${API_ENDPOINTS.LABOR_DETAIL}?orgId=${orgId}&locationId=${locationId}&year=${year}&month=${month}`
  );
  return response.data;
}

// ============ Revenue Forecast Types ============

export type ForecastMode = 'basic' | 'stat_account';

export interface StatAccountMapping {
  id: number;
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  operation: 'add' | 'subtract';
  revenueChartOfAccountId?: number | null;
  revenueAccountNumber?: string | null;
  revenueAccountName?: string | null;
}

export interface StatAccountInput {
  rn: number;
  adr: number;
  chartOfAccountId?: number;        // stat account's own GL account (revenue target fallback)
  revenueChartOfAccountId?: number; // explicitly mapped revenue GL account
}

export interface RevenueContextAccount {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  lyPct: number;
  lyAmt: number;
  budgetPct: number;
  budgetAmt: number;
}

export interface RevenueContextDaily {
  day: number;
  grpRN: number;
  transRN: number;
  grpRevenue: number;
  transRevenue: number;
}

export interface RevenueContext {
  totalRooms: number;
  availableRoomNights: number;
  daysInMonth: number;
  ly: {
    monthly: { grpRN: number; transRN: number; grpRevenue: number; transRevenue: number; totalRevenue: number };
    daily: RevenueContextDaily[];
  };
  otb: {
    monthly: { grpRN: number; transRN: number; grpRevenue: number; transRevenue: number; totalRevenue: number };
    daily: RevenueContextDaily[];
  };
  lm?: {
    monthly: { grpRN: number; transRN: number; grpRevenue: number; transRevenue: number; totalRevenue: number };
    daily: RevenueContextDaily[];
  };
  budget?: {
    grpRN: number;
    transRN: number;
    revenue: number;
    adr: number;
  };
  accounts: RevenueContextAccount[];
  statAccounts?: StatAccountMapping[];
}

export interface StatAccountHistoryEntry {
  rn: number;
  revenue: number;
}

export interface StatAccountHistoryAccount {
  statAccountId: number;
  chartOfAccountId: number;
  revenueChartOfAccountId: number | null;
  /** Keyed by 'YYYY-M' (no zero padding), e.g. '2025-8' */
  months: Record<string, StatAccountHistoryEntry>;
}

export interface StatAccountHistoryResponse {
  accounts: StatAccountHistoryAccount[];
}

export interface StatAccountDailyActualsResponse {
  days: { day: number; rn: number; revenue: number }[];
}

export interface RevenueInputsResponse {
  groupRN: number | null;
  groupADR: number | null;
  transRN: number | null;
  transADR: number | null;
  distributionMethod: string | null;
  distributions: { chartOfAccountId: number; pct: number }[];
  spreadMethod: string | null;
  savedDailyData: { day: number; groupRN: number; groupADR: number; transRN: number; transADR: number }[];
  mode?: ForecastMode;
  statAccountInputs?: Record<number, StatAccountInput>;
}

export interface ApplyRevenueForecastParams {
  versionId: number;
  locationId: number;
  orgId: number;
  year: number;
  month: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  inputs: { groupRN: number; groupADR: number; transRN: number; transADR: number };
  dailyData: { day: number; groupRN: number; groupADR: number; transRN: number; transADR: number }[];
  spreadMethod: string;
  distribution: { chartOfAccountId: number; pct: number }[];
  distributionMethod: 'ly_pct' | 'budget_pct' | 'manual';
  availableRoomNights?: number;
  occupiedRoomNights?: number;
  mode?: ForecastMode;
  statAccountInputs?: Record<number, StatAccountInput>;
}

export interface ApplyRevenueForecastResponse {
  success: boolean;
  accountsUpdated: number;
  daysWritten: number;
  monthlyTotal: number;
}

// ============ Revenue Forecast API Functions ============

export async function getRevenueContext(params: {
  versionId: number;
  locationId: number;
  orgId: number;
  year: number;
  month: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
}): Promise<RevenueContext> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getRevenueContext`, params);
  return response.data;
}

/** Trailing per-stat-account monthly actuals (RN + linked revenue) for budget-mode references and seeding.
 *  Pass the department so unlinked stat accounts resolve revenue by name match, like Apply does. */
export async function getStatAccountHistory(params: {
  orgId: number;
  locationId: number;
  months?: number;
  departmentName?: string;
  chartOfAccountDepartmentId?: number;
}): Promise<StatAccountHistoryResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getStatAccountHistory`, params);
  return response.data;
}

export interface StatAccountExportRow {
  statAccountId: number | null;
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  department: string | null;
  mappedType: string;
  operation: 'add' | 'subtract';
  revenueChartOfAccountId: number | null;
  revenueDepartment?: string | null;
  revenueDepartmentId?: number | null;
  actuals: Record<string, number>;
  inputs: Record<string, number>;
  /** Month number (1-12) → ADR keyed in stat-account mode. */
  adrInputs?: Record<string, number>;
  /** Month number (1-12) → value keyed in the Other Statistics section (non-room-night accounts). */
  values?: Record<string, number>;
}

/** Stat-account rows for the raw data export: GL actuals per month plus the
 *  room nights keyed in stat-account mode, for one version-year. */
export async function getStatAccountExport(params: {
  orgId: number;
  locationId: number;
  versionId?: number | string | null;
  year: number;
  /** On-screen projections so driven Other Statistics resolve like the grid. */
  projected?: StatProjectedContext | null;
  /** Keep every stat-typed account, even with no actuals and no plan (Export tab). */
  includeInactive?: boolean;
}): Promise<{ accounts: StatAccountExportRow[] }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getStatAccountExport`, params);
  return response.data;
}

// ── Export formats (org-config budgetExportFormats) ─────────────────────────

export interface ExportFormatsResponse {
  canEdit: boolean;
  configured: boolean;
  formats: Record<ExportFormatTarget, ExportFormatSlots>;
}

/** The org default + this location's override for every export target. */
export async function getExportFormats(orgId: number, locationId?: number | null): Promise<ExportFormatsResponse> {
  const response = await api.get(API_ENDPOINTS.FORECAST_V3_EXPORT_FORMATS, { params: { orgId, locationId: locationId || undefined } });
  return response.data;
}

/** Save (or with format null, remove) the org default or a location override. Org admins only. */
export async function saveExportFormat(params: {
  orgId: number;
  target: ExportFormatTarget;
  scope: 'org' | 'location';
  locationId?: number | null;
  format: BudgetExportFormat | null;
}): Promise<{ ok: boolean; formats: Record<ExportFormatTarget, ExportFormatSlots> }> {
  const response = await api.put(API_ENDPOINTS.FORECAST_V3_EXPORT_FORMATS, params);
  return response.data;
}

// ── Stat-account mapping panel (revenue modal) ──────────────────────────────

export interface StatMappingCategory {
  mappingId: number;
  mappedType: string;
  operation: 'add' | 'subtract';
  revenueChartOfAccountId: number | null;
}
export interface StatMappingCandidate {
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  department: string | null;
  /** Room-stat categories this account already sits under (any mapped type). */
  categories: StatMappingCategory[];
  isOccupied: boolean;
}
export interface StatMappingOptions {
  mappedTypes: { id: number; mappedType: string; operation: string; roomStatTypeName: string | null }[];
  occupiedMappedTypeId: number | null;
  candidates: StatMappingCandidate[];
}

export async function getStatMappingOptions(params: { orgId: number; locationId: number }): Promise<StatMappingOptions> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getStatMappingOptions`, params);
  return response.data;
}

/** Add a COA account as an Occupied Rooms segment (reuses / creates the location's mapped type). */
export async function addStatAccountMapping(params: {
  orgId: number;
  locationId: number;
  chartOfAccountId: number;
  revenueChartOfAccountId?: number | null;
  operation?: 'add' | 'subtract';
}): Promise<{ id: number; chartOfAccountId: number; revenueChartOfAccountId: number | null; operation: string; mappedTypeCreated: boolean }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=addStatAccountMapping`, params);
  return response.data;
}

export async function removeStatAccountMapping(params: { orgId: number; locationId: number; mappingId: number }): Promise<{ removed: boolean }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=removeStatAccountMapping`, params);
  return response.data;
}

// ── Other Statistics (keyed values on any non-room-night stat account) ──────

export interface StatValueAccount {
  chartOfAccountId: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  department: string | null;
  /** Room-stat categories the account sits under (never Occupied/Available Rooms — those live in Occupancy). */
  categories: string[];
  operation: 'add' | 'subtract';
}

/** Month number (1-12) → value, keyed by chartOfAccountId. */
export type StatValueSeries = Record<string, Record<string, number>>;

export type StatDriverType =
  | 'manual' | 'ly_actual' | 'ttm_avg' | 'ttm_seasonal' | 'budget' | 'ly_budget' | 'budget_spread'
  | 'annual_spread' | 'per_orn' | 'per_arn' | 'per_revenue';
export type StatRateSource = 'ly' | 'ttm' | 'manual';

/** Driver catalog entry (served by the API so the picker and the engine agree). */
export interface StatDriverDef {
  id: StatDriverType;
  group: string;
  label: string;
  tag: string;
  desc: string;
  /** What driverInput means for this driver. */
  input: 'adjustPct' | 'annualTotal' | 'rate' | 'dollarsPerUnit' | null;
  rateSources?: StatRateSource[];
  needsSourceAccounts?: boolean;
}

export interface StatDriverConfig {
  driverType: StatDriverType;
  driverInput: number | null;
  rateSource: StatRateSource | null;
  sourceChartOfAccountIds: number[];
  notes?: string | null;
}

/** Room-night and revenue projections the grid already has on screen, so the
 *  server drives stats off the same numbers. Month keys 'jan'.. or 1..12. */
export interface StatProjectedContext {
  occupiedRooms: Record<string, number>;
  availableRooms: Record<string, number>;
  revenue: Record<string, Record<string, number>>;
}

export interface StatValuesResponse {
  accounts: (StatValueAccount & { driver: StatDriverConfig | null })[];
  /** Values keyed on this version (month overrides). */
  values: StatValueSeries;
  /** What each account's driver produces (null months = driver has nothing). */
  driven: StatValueSeries;
  /** Per account → month → one-line explanation of the driven value. */
  notes: Record<string, Record<string, string>>;
  warnings: Record<string, string[]>;
  drivers: StatDriverDef[];
  closedThrough: { year: number; month: number } | null;
  /** GL actuals for the version year / the prior year. */
  actuals: StatValueSeries;
  lyActuals: StatValueSeries;
  /** financialBudget for the version year / the prior year. */
  budget: StatValueSeries;
  lyBudget: StatValueSeries;
  /** False until the forecastStatValue / forecastStatDriver tables have been deployed. */
  tableReady?: boolean;
}

export async function getStatValues(params: {
  orgId: number;
  locationId: number;
  versionId?: number | string | null;
  year: number;
  projected?: StatProjectedContext | null;
}): Promise<StatValuesResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getStatValues`, params);
  return response.data;
}

/** Set (or clear with driverType 'manual') one stat account's driver on a draft version. */
export async function saveStatDriver(params: {
  versionId: number | string;
  chartOfAccountId: number;
  driverType: StatDriverType;
  driverInput?: number | null;
  rateSource?: StatRateSource | null;
  sourceChartOfAccountIds?: number[];
  notes?: string | null;
}): Promise<{ success: boolean; batchId: string; driver: StatDriverConfig | null }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=saveStatDriver`, params);
  return response.data;
}

export interface StatValueItem {
  chartOfAccountId: number;
  year: number;
  month: number;
  /** null clears the keyed value. */
  value: number | null;
  notes?: string | null;
}

export interface SaveStatValuesResponse {
  success: boolean;
  batchId: string;
  applied: number;
  created: number;
  updated: number;
  deleted: number;
  skippedLocked: { chartOfAccountId: number; year: number; month: number }[];
}

/** Bulk upsert / clear keyed stat values on a draft version. */
export async function saveStatValues(params: { versionId: number | string; items: StatValueItem[]; batchId?: string }): Promise<SaveStatValuesResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=saveStatValues`, params);
  return response.data;
}

/** Day-grain actuals for one month from [Transaction] only (stat RN + resolved revenue).
 *  Months whose journals post month-end summaries return few/no day rows. */
export async function getStatAccountDailyActuals(params: {
  orgId: number;
  locationId: number;
  year: number;
  month: number;
  departmentName?: string;
  chartOfAccountDepartmentId?: number;
}): Promise<StatAccountDailyActualsResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getStatAccountDailyActuals`, params);
  return response.data;
}

export async function getRevenueInputs(params: {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  year: number;
  month: number;
}): Promise<RevenueInputsResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=getRevenueInputs`, params);
  return response.data;
}

export async function saveRevenueInputs(params: {
  versionId: number;
  locationId: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  year: number;
  month: number;
  groupRN: number;
  groupADR: number;
  transRN: number;
  transADR: number;
  mode?: ForecastMode;
  statAccountInputs?: Record<number, StatAccountInput>;
}): Promise<{ success: boolean }> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=saveRevenueInputs`, params);
  return response.data;
}

export async function applyRevenueForecast(params: ApplyRevenueForecastParams): Promise<ApplyRevenueForecastResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=applyRevenueForecast`, params);
  return response.data;
}

export interface BulkApplyRevenueForecastParams {
  versionId: number;
  locationId: number;
  orgId: number;
  year: number;
  departmentName: string;
  chartOfAccountDepartmentId?: number;
  months: {
    month: number;
    inputs: { groupRN: number; groupADR: number; transRN: number; transADR: number };
    dailyData: { day: number; groupRN: number; groupADR: number; transRN: number; transADR: number }[];
    spreadMethod: string;
    distribution: { chartOfAccountId: number; pct: number }[];
    distributionMethod: 'ly_pct' | 'budget_pct' | 'manual';
    availableRoomNights?: number;
    occupiedRoomNights?: number;
  }[];
}

export interface BulkApplyRevenueForecastResponse {
  success: boolean;
  monthsApplied: number;
  totalAccountsUpdated: number;
  totalDaysWritten: number;
}

export async function bulkApplyRevenueForecast(params: BulkApplyRevenueForecastParams): Promise<BulkApplyRevenueForecastResponse> {
  const response = await api.post(`${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=bulkApplyRevenueForecast`, params);
  return response.data;
}

// ============ AI Build (Build with AI) API ============

export interface AiBuildPreviewStreamOptions {
  versionId: number;
  onProgress?: (progress: AiBuildProgress) => void;
  onComplete?: (result: AiBuildPreviewResult) => void;
  onError?: (error: string) => void;
}

export interface AiBuildCommitAssignment {
  chartOfAccountId: number;
  driverType: string;
  driverInput: number | null;
  inputMode: 'monthly';
  effectiveStartMonth: 1;
  effectiveEndMonth: 12;
  selectedDepartments: string[] | null;
  sourceChartOfAccountIds: null;
  fallbackDriverType: null;
  // Budget-mode per-position Labor Config payload ('Labor Config' driver)
  laborPositionConfigs?: LaborPositionConfig[] | null;
}

export interface AiBuildCommitParams {
  versionId: number;
  assignments: AiBuildCommitAssignment[];
  // Budget-mode rooms baseline from the preview — persisted as
  // forecastRoomStatOverride rows in the same revertable batch.
  roomStatSeed?: AiBuildRoomStatSeed | null;
}

export interface AiBuildCommitResponse {
  success: boolean;
  updated: number;
  failed: number;
  results: { chartOfAccountId: number; status: 'success' | 'error'; error?: string }[];
  batchId: string;
  roomStatsSeeded?: number;
}

export interface RevertAiBatchResponse {
  success: boolean;
  reverted: number;
  versionId: number;
}

export interface AiBatchSummary {
  batchId: string;
  versionId: number;
  rowCount: number;
  createdAt: string;
  reverted: boolean;
  userEmail: string;
}

/**
 * Build the auth headers a raw fetch needs (the axios instance normally adds
 * these via interceptor). Mirrors the SSE stream pattern in utils/api.js
 * (queryHotelDataStream / dashboardDailyBriefing).
 */
async function buildStreamAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };

  const accessTokenKey = Object.keys(localStorage).find(k => k.endsWith('.accessToken'));
  const idTokenKey = Object.keys(localStorage).find(k => k.endsWith('.idToken'));
  const accessToken = accessTokenKey ? localStorage.getItem(accessTokenKey) : null;
  const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : null;

  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (idToken) headers['x-id-token'] = idToken;

  try {
    // @ts-ignore - JavaScript module without types
    const { getEmulatedUserForApi } = await import('../../../utils/emulation');
    const emulatedUser = getEmulatedUserForApi();
    if (emulatedUser) {
      headers['x-user-id'] = emulatedUser.userId;
      headers['x-user-email'] = emulatedUser.userEmail;
      headers['x-emulating'] = 'true';
    } else if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (payload.username || payload.sub) headers['x-user-id'] = payload.username || payload.sub;
        if (payload.email) headers['x-user-email'] = payload.email;
      } catch { /* ignore malformed token */ }
    }
  } catch { /* ignore emulation import failure */ }

  return headers;
}

/**
 * Start the AI build preview as an SSE stream (POST ?action=aiBuildPreview).
 * Native EventSource can't POST or send auth headers, so this uses a raw
 * fetch + reader loop, parsing `event:` / `data:` framed SSE messages.
 *
 * Events:
 *   progress -> { stage: 'analyzing' } then { stage: 'calculating', accountCount }
 *   complete -> { proposals, summary }
 *   error    -> { error }
 *
 * @returns abort function - call to cancel the stream
 */
export function aiBuildPreviewStream({ versionId, onProgress, onComplete, onError }: AiBuildPreviewStreamOptions): () => void {
  const controller = new AbortController();

  (async () => {
    try {
      const headers = await buildStreamAuthHeaders();
      const response = await fetch(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=aiBuildPreview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ versionId }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '');
        onError?.(text || `HTTP ${response.status}`);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawTerminalEvent = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE frames (event/data lines can split across chunks)
        let boundary: number;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);

          let event = 'message';
          let data = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) data += line.slice(5).trim();
          }
          if (!data) continue;

          let payload: any;
          try { payload = JSON.parse(data); } catch { continue; }

          if (event === 'progress') {
            onProgress?.(payload as AiBuildProgress);
          } else if (event === 'complete') {
            sawTerminalEvent = true;
            onComplete?.(payload as AiBuildPreviewResult);
          } else if (event === 'error') {
            sawTerminalEvent = true;
            onError?.(payload.error || 'AI build failed');
          }
        }
      }

      if (!sawTerminalEvent) {
        onError?.('Stream ended before results were received');
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        onError?.(e?.message || 'Stream failed');
      }
    }
  })();

  return () => controller.abort();
}

/**
 * Commit accepted AI build proposals as driver assignments (batched, undoable)
 */
export async function aiBuildCommit(params: AiBuildCommitParams): Promise<AiBuildCommitResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=aiBuildCommit`,
    params
  );
  return response.data;
}

/**
 * Revert an entire AI build commit batch (undo)
 */
export async function revertAiBatch(batchId: string): Promise<RevertAiBatchResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=revertAiBatch`,
    { batchId }
  );
  return response.data;
}

/**
 * Get summary info for a committed AI build batch
 */
export async function getAiBatchSummary(batchId: string): Promise<AiBatchSummary> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS}?action=getAiBatchSummary`,
    { batchId }
  );
  return response.data;
}

// ============ AI Rooms Forecast API ============

export interface AiRoomsForecastPreviewParams {
  versionId: number;
  asOfDate?: string;
  departmentName?: string;
  chartOfAccountDepartmentId?: number | null;
}

export interface AiRoomsForecastCommitDay {
  date: string;       // YYYY-MM-DD
  roomNights: number;
  adr: number;
}

export interface AiRoomsForecastCommitParams {
  versionId: number;
  days: AiRoomsForecastCommitDay[];  // non-actualized days only
  departmentName?: string;
  chartOfAccountDepartmentId?: number | null;
  clearOverrideMonths?: string[];  // 'YYYY-MM' — remove manual overrides on the target accounts (undoable)
}

export interface AiRoomsForecastCommitResponse {
  success: boolean;
  written: number;            // stay dates saved to the AI forecast source
  batchId: string;
  forecastSource: string;     // this version's source, 'lodgeic:ai_pickup_v<versionId>'
  departmentsUpdated: number;
  accountsDistributed: number;
  accountsOnOwnDriver: number;
  pinsCleared: number;        // pins retired from the old single-account apply
  overridesCleared?: number;  // manual overrides removed (clearOverrideMonths)
}

/**
 * Preview the OTB + learned-pickup rooms forecast for a version.
 * Returns { available: false, reason } when there isn't enough OTB
 * snapshot history to learn a pickup curve.
 */
export async function aiRoomsForecastPreview(params: AiRoomsForecastPreviewParams): Promise<AiRoomsForecastPreviewResult> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=aiRoomsForecastPreview`,
    params
  );
  return response.data;
}

/**
 * Apply the AI rooms forecast: saves it as this version's
 * 'lodgeic:ai_pickup_v<versionId>' Room Forecast source and points the rooms revenue department driver at it, so it
 * distributes across the department like any other rooms driver
 * (batched, undoable via revertAiBatch)
 */
export async function aiRoomsForecastCommit(params: AiRoomsForecastCommitParams): Promise<AiRoomsForecastCommitResponse> {
  const response = await api.post(
    `${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}?action=aiRoomsForecastCommit`,
    params
  );
  return response.data;
}

export async function updateStatAccountMapping(
  id: number,
  data: { revenueChartOfAccountId: number | null }
): Promise<void> {
  await api.put(`${API_ENDPOINTS.ROOM_STAT_ACCOUNT_MAPPINGS}?id=${id}`, data);
}
